import {
  RangeFilterType,
  SelectorFilterType,
  SelectorOption,
} from "@/pages/Players/FiltersPanel/filterTypes";
import { ClubData } from "@repo/shared/api";
import {
  CardFont,
  Cosmetic,
  EvoPathId,
  Foil,
  MetaRating,
  ObjectiveDisplay,
  PriceInfo,
} from "@repo/shared/types";
import { inGameAttributesDict } from "@repo/shared/utils";
import { captureMessage } from "@sentry/react";

import { Formations } from "../../api/privateRequests/squad-builder/get-formations";
import {
  ChemPlayer,
  ChemistryProfiles,
  PlayersChemistry,
  calculateChemistry,
} from "../../functions/calculateChemistry";
import { Tactic } from "../tactics/tacticsSlice";
import { findMetaRating } from "./findMetaRating";

// Moved here from playersSlice.ts to avoid circular dependency
export const bodyTypeIdToName: Record<number, string[]> = {
  1: ["Avg & Lean"],
  2: ["Avg & Normal"],
  3: ["Avg & Stocky"],
  4: ["Tall & Lean"],
  5: ["Tall & Normal"],
  6: ["Tall & Stocky"],
  7: ["Short & Lean"],
  8: ["Short & Normal"],
  9: ["Short & Stocky"],
  11: ["Very Tall & Lean"],
  12: ["Unique", "Lean"],
  13: ["Unique", "Normal"],
  14: ["Unique", "Stocky"],
  15: ["-", "-"],
};

export type Manager = {
  country?: number;
  league?: number;
};

export type Scope = "market" | "both" | "club";
export type Metal = "bronze" | "silver" | "gold";
export const numberToMetal = (index: number): Metal => {
  switch (index) {
    case 0:
      return "gold";
    case 1:
      return "silver";
    case 2:
      return "bronze";
    default:
      return undefined;
  }
};

export type Playstyle = {
  id: number;
  label: string;
  category: string;
};

export type Squad = {
  players: SquadBuilderPlayerType[];
  manager: Manager;
  chemistry: ChemistryOverview;
  suggestedSquadChemistry: number;
  metaRating: number;
  tactic?: Tactic;
};

export type RatingFilter = {
  min?: number;
  max?: number;
};

export const emptyPlayer = {} as SquadBuilderPlayerType;
export const setEmptySquad = (
  playerRoles?: { playerRoleId: number }[],
  chosenIngameFormation: number = 0,
) => ({
  chosenIngameFormation,
  players: (playerRoles as SquadBuilderPlayerType[]) ?? [
    emptyPlayer,
    emptyPlayer,
    emptyPlayer,
    emptyPlayer,
    emptyPlayer,
    emptyPlayer,
    emptyPlayer,
    emptyPlayer,
    emptyPlayer,
    emptyPlayer,
    emptyPlayer,
  ],
  manager: {
    country: undefined,
    league: undefined,
  },
  chemistry: {
    teamChemistry: 0,
    playersOutOfPosition: [],
    playersChemistry: {} as PlayersChemistry,
    summary: {
      countryCounter: {},
      leagueCounter: {},
      clubCounter: {},
    },
  },
  suggestedSquadChemistry: 0,
  metaRating: 0,
  ingameFormations: [],
});

export type SquadBuilderPlayerType = {
  resourceId: number;
  assetId?: number;
  eaPlayerId?: number;
  clubId: number;
  leagueId: number;
  countryId: number;
  rating: number;
  playerRoleId: number;
  preferredPosition: string;
  possiblePositions: string[];
  owned: boolean;
  cardName: string;
  cardFont?: CardFont;
  foil?: Foil;
  cosmetics?: Cosmetic[];
  untradeable: boolean;
  firstOwner?: boolean;
  versionId: number;
  metalId: number;
  attributes: number[];
  rolesPlus: number[];
  chemIndex?: number;
  playStylesPlus: number[];
  position?: string;
  userOwned?: boolean;
  isAIPlayer?: boolean;
  metaRatings: MetaRating[];
  hasDynamicImage: boolean;
  playerUrl: string;
  priceInfo?: PriceInfo;
  objectiveDisplay?: ObjectiveDisplay;
  isObjectivePlayer: boolean;
  isSbcPlayer: boolean;
  isLoanPlayer?: boolean;
  numberOfLoanedGames?: number;
  evolutionPath?: number[];
  pathwayPath?: number[][];
  evoPathId?: EvoPathId;
  chemistry?: number;
  chemstyleId?: number;
  teamMetaRating?: number;
  teamChemistry?: number;
  skillMoves?: number;
  weakFoot?: number;
  isEvoPlayer?: boolean;
};

export const chemStylesToIcon = {
  250: "\uE033",
  251: "\uE044",
  252: "\uE038",
  253: "\uE036",
  254: "\uE03F",
  255: "\uE03C",
  256: "\uE031",
  257: "\uE030",
  258: "\uE040",
  259: "\uE03E",
  260: "\uE037",
  261: "\uE041",
  262: "\uE03B",
  263: "\uE039",
  264: "\uE032",
  265: "\uE02F",
  266: "\uE03D",
  267: "\uE035",
  268: "\uE042",
  269: "\uE045",
  270: "\uE043",
  271: "\uE034",
  272: "\uE03A",
  273: "\uE033",
};

export type PlayerToInclude = {
  playerRoleId: number;
  player?: SquadBuilderPlayerType;
  userAddedPlayerOnRole?: boolean;
};

export type FromBackendSquadBuilderPlayer = {
  resourceId: number;
  canEvolve: boolean;
  eaPlayerId?: number;
  chemIndex: number;
  inGameIndex: number;
  cardName: string;
  cardFont?: CardFont;
  countryId: number;
  leagueId: number;
  clubId: number;
  rating: number;
  metalId: number;
  versionId: number;
  attributes: number[];
  isSbcPlayer: boolean;
  isObjectivePlayer: boolean;
  preferredPosition: string;
  possiblePositions: string[];
  untradeable: boolean;
  owned: boolean;
  assetId: number;
  hasDynamicImage: boolean;
  playerUrl: string;
  metaRatings: MetaRating[];
  playStylesPlus: number[];
};

export type ChemSummary = {
  countryCounter: { [key: string]: number };
  leagueCounter: { [key: string]: number };
  clubCounter: { [key: string]: number };
};

export type ChemistryOverview = {
  teamChemistry: number;
  playersOutOfPosition: ChemPlayer[];
  playersChemistry: PlayersChemistry;
  summary: ChemSummary;
};

type MetaRatingCalculationPlayer = {
  clubId: number;
  leagueId: number;
  countryId: number;
  positionIndex: number;
  possiblePositions: string[];
  versionId: number;
  playerRoleId: number;
  metaRatings: MetaRating[];
};

/** Squad meta rating weight per player role id; roles without one count as 1. */
export const roleWeightsByRoleId = (
  playerRoles: { id: number; squadMetaRatingWeight?: number }[],
): Record<number, number> =>
  Object.fromEntries(
    playerRoles.map((pr) => [pr.id, pr.squadMetaRatingWeight ?? 1]),
  );

// Role-weighted mean over the 11 slots: an empty slot adds 0 to the numerator
// and its role's weight to the denominator.
export const getTeamMetaRating = ({
  playersChemistry,
  players,
  playerRoleIds,
  roleWeights,
}: {
  playersChemistry: PlayersChemistry;
  players: MetaRatingCalculationPlayer[];
  playerRoleIds: number[];
  roleWeights: Record<number, number>;
}) => {
  let metaRatingSum = 0;
  let weightSum = 0;
  for (let i = 0; i < 11; i++) {
    const player = players[i];
    const weight = roleWeights[playerRoleIds[i]] ?? 1;
    let metaRating: number = 0;

    if (player?.metaRatings?.length > 0) {
      try {
        metaRating =
          findMetaRating({
            metaRatings: player?.metaRatings,
            playerRoleId: player?.playerRoleId,
            chemistry: playersChemistry[i],
          })?.metaRating || 0;
      } catch (error) {
        captureMessage(error instanceof Error ? error.message : String(error), {
          level: "error",
          tags: {
            component: "getTeamMetaRating",
          },
        });
      }
    }

    metaRatingSum += metaRating * weight;
    weightSum += weight;
  }
  return weightSum > 0 ? metaRatingSum / weightSum : 0;
};

export const getChemistry = ({
  players,
  manager,
  formationPositions,
  chemistryProfiles,
  clubLinks,
}: {
  players: ChemPlayer[];
  manager: Manager;
  formationPositions: string[];
  chemistryProfiles: ChemistryProfiles;
  clubLinks?: Record<number, number>;
}): ChemistryOverview => {
  const chemPlayers = [];
  for (let i = 0; i < 11; i++) {
    const p = players[i];
    if (p?.possiblePositions.length > 0)
      chemPlayers.push({
        clubId: p.clubId,
        leagueId: p.leagueId,
        countryId: p.countryId,
        positionIndex: i,
        possiblePositions: p.possiblePositions,
        versionId: p.versionId,
      });
  }
  const chemistryObject = calculateChemistry({
    players: chemPlayers,
    formationPositions,
    manager,
    chemistryProfiles,
    clubLinks,
  });
  const teamChemistry = Object.values(chemistryObject.playersChemistry).reduce(
    (sum, chemistry) => sum + chemistry,
    0,
  );

  return {
    teamChemistry,
    playersOutOfPosition: chemistryObject.playersOutOfPosition,
    playersChemistry: chemistryObject.playersChemistry,
    summary: {
      countryCounter: chemistryObject.countryCounter,
      leagueCounter: chemistryObject.leagueCounter,
      clubCounter: chemistryObject.clubCounter,
    },
  };
};

export type RightPanelMode =
  | "default"
  | "add-player"
  | "add-player-filters"
  | "add-player-role"
  | "add-evo-player"
  | "suggested-squads"
  | "squad-overview"
  | "select-chem-style"
  | "add-more-filters";

export type LeftPanelMode = "chem-overview" | "choose-formation" | "tactics";

export interface ChoiceFilter<T extends String | Number> {
  values: T[];
}

export type MinMaxFilter = {
  min?: number;
  max?: number;
};

export type OtherAttributes = {
  skillMoves?: ChoiceFilter<number>;
  weakFoot?: ChoiceFilter<number>;
  preferredFoot?: ChoiceFilter<string>;
  height?: MinMaxFilter;
};

type InGameAttributes = {
  acceleration?: MinMaxFilter;
  sprintSpeed?: MinMaxFilter;
  agility?: MinMaxFilter;
  balance?: MinMaxFilter;
  jumping?: MinMaxFilter;
  stamina?: MinMaxFilter;
  strength?: MinMaxFilter;
  reactions?: MinMaxFilter;
  aggression?: MinMaxFilter;
  composure?: MinMaxFilter;
  interceptions?: MinMaxFilter;
  positioning?: MinMaxFilter;
  vision?: MinMaxFilter;
  ballControl?: MinMaxFilter;
  crossing?: MinMaxFilter;
  dribbling?: MinMaxFilter;
  finishing?: MinMaxFilter;
  freeKickAccuracy?: MinMaxFilter;
  headingAccuracy?: MinMaxFilter;
  longPassing?: MinMaxFilter;
  shortPassing?: MinMaxFilter;
  defensiveAwareness?: MinMaxFilter;
  shotPower?: MinMaxFilter;
  longShots?: MinMaxFilter;
  standingTackles?: MinMaxFilter;
  slidingTackles?: MinMaxFilter;
  volleys?: MinMaxFilter;
  curve?: MinMaxFilter;
  penalties?: MinMaxFilter;
  gkDiving?: MinMaxFilter;
  gkHandling?: MinMaxFilter;
  gkKicking?: MinMaxFilter;
  gkReflexes?: MinMaxFilter;
  gkPositioning?: MinMaxFilter;
  traits?: MinMaxFilter;
  specialties?: MinMaxFilter;
};

export const setEmptyInGameAttributes = (): InGameAttributes =>
  Object.keys(inGameAttributesDict).reduce((a, v) => ({ ...a, [v]: {} }), {});

export const setEmptyOtherAttributes = (): OtherAttributes =>
  Object.keys(otherAttributeSettings).reduce((a, v) => ({ ...a, [v]: [] }), {});

export const setEmptyAttributes = ({
  playStylesFilterOptions,
  playStylesPlusFilterOptions,
}: {
  playStylesFilterOptions?: SelectorOption[];
  playStylesPlusFilterOptions?: SelectorOption[];
}) => [
  {
    label: "PlayStyles",
    key: "playStyles",
    type: "SELECTOR_FILTER",
    options: playStylesFilterOptions || [],
    selectedOptions: [],
  } as SelectorFilterType,
  {
    label: "Playstyle+",
    key: "playStylesPlus",
    type: "SELECTOR_FILTER",
    options: playStylesPlusFilterOptions || [],
    selectedOptions: [],
  } as SelectorFilterType,
  {
    label: "Skill Moves",
    shortLabel: "SM",
    key: "skillMoves",
    type: "SELECTOR_FILTER",
    options: [
      { id: 1, label: "1", category: "Skill Moves" },
      { id: 2, label: "2", category: "Skill Moves" },
      { id: 3, label: "3", category: "Skill Moves" },
      { id: 4, label: "4", category: "Skill Moves" },
      { id: 5, label: "5", category: "Skill Moves" },
    ],
    selectedOptions: [],
  } as SelectorFilterType,
  {
    label: "Weak Foot",
    shortLabel: "WF",
    key: "weakFoot",
    type: "SELECTOR_FILTER",
    options: [
      { id: 1, label: "1", category: "Weak Foot" },
      { id: 2, label: "2", category: "Weak Foot" },
      { id: 3, label: "3", category: "Weak Foot" },
      { id: 4, label: "4", category: "Weak Foot" },
      { id: 5, label: "5", category: "Weak Foot" },
    ],
    selectedOptions: [],
  } as SelectorFilterType,
  {
    label: "Preferred Foot",
    shortLabel: "PF",
    key: "preferredFoot",
    type: "SELECTOR_FILTER",
    useLabelAsId: true,
    options: [
      { id: 0, label: "Left", category: "Preferred Foot" },
      { id: 1, label: "Right", category: "Preferred Foot" },
    ],
    selectedOptions: [],
  } as SelectorFilterType,
  ...Object.entries(inGameAttributesDict).map(
    ([key, attribute]) =>
      ({
        label: attribute.label,
        key,
        type: "RANGE_FILTER",
        range: [1, 99],
        minValue: null,
        maxValue: null,
      }) as RangeFilterType,
  ),
  {
    label: "Body Type",
    key: "body-types",
    type: "SELECTOR_FILTER",
    options: Object.entries(bodyTypeIdToName).map(([id, name]) => ({
      id: parseInt(id),
      label: name[0],
      category: "Body Type",
    })),
    selectedOptions: [],
  } as SelectorFilterType,
];

export type PlayerFilter = (RangeFilterType | SelectorFilterType)[];

// Filters set on one pitch spot
export const countActivePlayerFilters = (filters?: PlayerFilter) =>
  filters?.filter((pf) =>
    pf.type === "RANGE_FILTER"
      ? pf.maxValue || pf.minValue
      : pf.selectedOptions.length > 0,
  ).length ?? 0;

export const inGameAttributeCategories = [
  "PACE",
  "SHOOTING",
  "PASSING",
  "DRIBBLING",
  "DEFENDING",
  "PHYSICALITY",
  "GOALKEEPING",
] as const;

// To prevent duplicates in the filters (primarily due to dribblingTitle)

export type InGameAttributeCategory =
  (typeof inGameAttributeCategories)[number];

export const setEmptyPlayerFilters = ({
  playStylesFilterOptions,
  playStylesPlusFilterOptions,
}: {
  playStylesFilterOptions?: SelectorOption[];
  playStylesPlusFilterOptions?: SelectorOption[];
}) =>
  Array(11).fill(
    setEmptyAttributes({
      playStylesFilterOptions: playStylesFilterOptions || [],
      playStylesPlusFilterOptions: playStylesPlusFilterOptions || [],
    }),
  );

export const setEmptyBench: ({
  playerRoles,
}: {
  playerRoles: { playerRoleId: number; position: string }[];
}) => SquadBuilderPlayerType[] = ({ playerRoles }) => {
  return playerRoles.map((pr) => ({
    ...emptyPlayer,
    playerRoleId: pr.playerRoleId,
    position: pr.position,
  }));
};

type OtherAttributeSetting = {
  label: string;
  options?: (string | number)[];
  exclusive?: boolean;
};

export const otherAttributeSettings: { [key: string]: OtherAttributeSetting } =
  {
    skillMoves: {
      label: "Skill Moves",
      options: [1, 2, 3, 4, 5],
    },
    weakFoot: {
      label: "Weak Foot",
      options: [1, 2, 3, 4, 5],
    },
    preferredFoot: {
      label: "Preferred Foot",
      options: ["Left", "Right"],
      exclusive: true,
    },
    height: { label: "Height" },
  };

export const buildClubLinks = (clubs: ClubData[]): Record<number, number> => {
  const links: Record<number, number> = {};
  for (const club of clubs) {
    const linked = club.linkedClubIds?.[0];
    if (linked == null) continue;
    const canonical = Math.min(club.id, linked);
    links[club.id] = canonical;
    links[linked] = canonical;
  }
  return links;
};

export const calculateMetaRatingAndChemistry = ({
  players,
  manager,
  playerRoleIds,
  formationId,
  chemistryProfiles,
  formations,
  clubLinks,
  roleWeights,
}: {
  players: SquadBuilderPlayerType[];
  manager: Manager;
  playerRoleIds: number[];
  formationId: string;
  chemistryProfiles: ChemistryProfiles;
  formations: Formations;
  clubLinks?: Record<number, number>;
  roleWeights: Record<number, number>;
}) => {
  const formation = formations[formationId];
  if (!formation) {
    throw new Error(`Formation ${formationId} not found`);
  }
  const transformedPlayers: (MetaRatingCalculationPlayer | null)[] =
    players.map((p, index) => {
      if (!p) return null;
      return {
        clubId: p.clubId,
        leagueId: p.leagueId,
        countryId: p.countryId,
        positionIndex: index,
        possiblePositions: p.possiblePositions || [],
        versionId: p.versionId,
        playerRoleId: playerRoleIds[index],
        metaRatings: p.metaRatings,
      };
    });

  const chemistry = getChemistry({
    players: transformedPlayers,
    manager,
    formationPositions: formation.positions,
    chemistryProfiles,
    clubLinks,
  });

  const teamMetaRating = getTeamMetaRating({
    playersChemistry: chemistry.playersChemistry,
    players: transformedPlayers,
    playerRoleIds,
    roleWeights,
  });
  return { teamMetaRating, chemistry };
};

export const prepareFiltesForAPI = (playerFilters: PlayerFilter[]) =>
  playerFilters.reduce((acc, pf) => {
    const inGameAttributes = pf.reduce((acc, f) => {
      if (
        Object.keys(inGameAttributesDict).includes(f.key) &&
        f.type === "RANGE_FILTER" &&
        (f.minValue || f.maxValue)
      ) {
        acc[f.key] = {
          min: f.minValue,
          max: f.maxValue,
        };
      }
      return acc;
    }, {});
    const otherAttributes = pf.reduce((acc, f) => {
      if (
        !Object.keys(inGameAttributesDict).includes(f.key) &&
        f.type === "SELECTOR_FILTER" &&
        f.selectedOptions.length > 0
      ) {
        acc[f.key] = f.selectedOptions.map((so) => {
          if (f.useLabelAsId) {
            return f.options.find((o) => o.id === so)?.label;
          }
          return f.options.find((o) => o.id === so)?.id;
        });
      }
      return acc;
    }, {});

    if (
      Object.keys(inGameAttributes).length > 0 ||
      Object.keys(otherAttributes).length > 0
    )
      acc.push({
        inGameAttributes,
        otherAttributes,
      });
    else acc.push(null);

    return acc;
  }, [] as any[]);

export const defaultTactic: Tactic = {
  name: "FC Preset",
  description:
    "A direct approach which focuses on breaking down defenses with quick and incisive attacking runs",
  popularityScore: 0,
  eaCode: "f44f@Nvnp97f",
  logoUrl: `${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/tactics/logos/preset2.png`,
  author: "EA Sports",
  tags: ["Preset"],
  teamTactics: {
    buildUpStyle: "Balanced",
    formationId: "f433",
    defensiveApproach: "Balanced",
    lineHeight: 50,
  },
  isTrending: false,
  isFavorite: false,
  isNew: false,
  publishedAt: 0,

  playerRoles: [
    { focus: "Balanced", playerRoleId: 1 },
    { focus: "Balanced", playerRoleId: 3 },
    { focus: "Defend", playerRoleId: 11 },
    { focus: "Defend", playerRoleId: 11 },
    { focus: "Balanced", playerRoleId: 7 },
    { focus: "Balanced", playerRoleId: 18 },
    { focus: "Defend", playerRoleId: 19 },
    { focus: "Attack", playerRoleId: 21 },
    { focus: "Balanced", playerRoleId: 36 },
    { focus: "Attack", playerRoleId: 42 },
    { focus: "Balanced", playerRoleId: 39 },
    { focus: "Balanced", playerRoleId: 35 },
    { focus: "Attack", playerRoleId: 41 },
    { focus: "Balanced", playerRoleId: 38 },
    { focus: "Attack", playerRoleId: 21 },
    { focus: "Defend", playerRoleId: 11 },
    { focus: "Balanced", playerRoleId: 3 },
    { focus: "Defend", playerRoleId: 19 },
    { focus: "Balanced", playerRoleId: 35 },
    { focus: "Attack", playerRoleId: 41 },
    { focus: "Balanced", playerRoleId: 38 },
    { focus: "Balanced", playerRoleId: 18 },
    { focus: "Defend", playerRoleId: 11 },
  ],
};

export const BASIC_CHEMSTYLE = 250;
